import { Route, Switch, Redirect, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider, useAuth } from "./lib/auth";
import { OrganizationProvider } from "./lib/organization-context";
import { BranchProvider } from "./lib/branch-context";
import { ThemeProvider } from "./lib/theme-provider";
import { LocaleProvider } from "./lib/i18n/LocaleProvider";
import { Toaster } from "./components/ui/toaster";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/ui/error-boundary";

const AuthPage = lazy(() => import("./pages/AuthPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ContentPage = lazy(() => import("./pages/ContentPage"));
const SchedulingPage = lazy(() => import("./pages/SchedulingPage"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const SocialAccountsPage = lazy(() => import("./pages/SocialAccountsPage"));
const AdminPanelPage = lazy(() => import("./pages/AdminPanelPage"));
const WorkflowMonitorPage = lazy(() => import("./pages/WorkflowMonitorPage"));
const ClientsPage = lazy(() => import("./pages/admin/ClientsPage"));
const AddressMigrationPage = lazy(() => import("./pages/admin/AddressMigrationPage"));
const LogsPage = lazy(() => import("./pages/admin/LogsPage"));
const EndCardPage = lazy(() => import("./pages/admin/EndCardPage"));
const MusicPage = lazy(() => import("./pages/admin/MusicPage"));
const SettingsPage = lazy(() => import("./pages/admin/SettingsPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const ContentTypesPage = lazy(() => import("./pages/admin/ContentTypesPage"));
const VideoStyleParamsPage = lazy(() => import("./pages/admin/VideoStyleParamsPage"));
const VideoStyle2BuilderPage = lazy(() => import("./pages/admin/VideoStyle2BuilderPage"));
const MarketingDemoPage = lazy(() => import("./pages/admin/MarketingDemoPage"));
const HealthMonitorPage = lazy(() => import("./pages/admin/HealthMonitorPage"));
const StockLibraryPage = lazy(() => import("./pages/admin/StockLibraryPage"));
const OrganizationSettingsPage = lazy(() => import("./pages/OrganizationSettingsPage"));
const DirectUploadPage = lazy(() => import("./pages/DirectUploadPage"));
const BranchManagementPage = lazy(() => import("./pages/admin/BranchManagementPage"));
const LeadMagnetsPage = lazy(() => import("./pages/LeadMagnetsPage"));
const OAuthCallbackPage = lazy(() => import("./pages/OAuthCallbackPage"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-2">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [location] = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    const currentPath = location + window.location.search;
    const returnUrl = encodeURIComponent(currentPath);
    return <Redirect to={`/auth?returnUrl=${returnUrl}`} />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function AppRoutes() {
  const { user } = useAuth();

  const getReturnUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      return returnUrl;
    }
    return '/';
  };

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/auth">
          {user ? <Redirect to={getReturnUrl()} /> : <AuthPage />}
        </Route>

        <Route path="/oauth/callback">
          <OAuthCallbackPage />
        </Route>
        
        <Route path="/">
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        </Route>

        <Route path="/content">
          <ProtectedRoute>
            <ContentPage />
          </ProtectedRoute>
        </Route>

        {/* Activity replaced by Content — keep redirect for old links/bookmarks */}
        <Route path="/videos">
          <Redirect to="/content" />
        </Route>

        <Route path="/scheduling">
          <ProtectedRoute>
            <SchedulingPage />
          </ProtectedRoute>
        </Route>

        <Route path="/review">
          <ProtectedRoute>
            <ReviewPage />
          </ProtectedRoute>
        </Route>

        <Route path="/upload">
          <ProtectedRoute>
            <DirectUploadPage />
          </ProtectedRoute>
        </Route>

        <Route path="/accounts">
          <ProtectedRoute>
            <SocialAccountsPage />
          </ProtectedRoute>
        </Route>

        {/* Old digest emails link /connections — keep redirect permanently */}
        <Route path="/connections">
          <Redirect to="/accounts" />
        </Route>

        {/* Posted-to-social absorbed into Content — keep redirect for old links */}
        <Route path="/posted">
          <Redirect to="/content" />
        </Route>

        <Route path="/workflows">
          <ProtectedRoute>
            <WorkflowMonitorPage />
          </ProtectedRoute>
        </Route>

        <Route path="/lead-magnets/:tab?">
          <ProtectedRoute>
            <LeadMagnetsPage />
          </ProtectedRoute>
        </Route>

        <Route path="/organization/settings/:section">
          <ProtectedRoute>
            <OrganizationSettingsPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/clients">
          <ProtectedRoute>
            <ClientsPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/address-migration">
          <ProtectedRoute>
            <AddressMigrationPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/logs">
          <ProtectedRoute>
            <LogsPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/end-card">
          <ProtectedRoute>
            <EndCardPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/music">
          <ProtectedRoute>
            <MusicPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/settings">
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/users">
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/content-types">
          <ProtectedRoute>
            <ContentTypesPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/video-styles">
          <ProtectedRoute>
            <VideoStyleParamsPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/video-style-2">
          <ProtectedRoute>
            <VideoStyle2BuilderPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/marketing-demo">
          <ProtectedRoute>
            <MarketingDemoPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/health">
          <ProtectedRoute>
            <HealthMonitorPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/stock-library">
          <ProtectedRoute>
            <StockLibraryPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin/branches">
          <ProtectedRoute>
            <BranchManagementPage />
          </ProtectedRoute>
        </Route>

        <Route path="/admin">
          <ProtectedRoute>
            <AdminPanelPage />
          </ProtectedRoute>
        </Route>

        <Route>
          <Redirect to="/" />
        </Route>
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="social-app-theme">
        <LocaleProvider enabled={false}>
          <AuthProvider>
            <OrganizationProvider>
              <BranchProvider>
                <AppRoutes />
                <Toaster />
              </BranchProvider>
            </OrganizationProvider>
          </AuthProvider>
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
